import { useState, useMemo, useEffect } from 'react';
import { useBookings } from '@hooks/useBookings';
import type { BookingStatus, Center } from '@api/types';
import * as Icons from '@components/Icons';
import { RowMenuWithActions } from './BookingRowMenu';
import { adminReportsApi } from '@api/client';
import { BookingDetailDrawer, fmtTime, STATUS_MAP, StatusPill, initials, tone } from './BookingDetailDrawer';
import { PageHead } from '@components/admin/PageHead';
import { useAuth } from '@hooks/useAuth';
import { useCenters } from '@hooks/useAdminResources';
import { useActiveCenter, ALL_CENTERS } from '@hooks/useActiveCenter';
import { todayInTimezone } from '@lib/timezone';
import { BookingFormDrawer } from './bookings/BookingFormDrawer';

// ── helpers ──────────────────────────────────────────────────────────────────
const MONTHS_ES = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];

function fmtDate(iso: string) {
  if (!iso) return '—';
  const [, m, d] = iso.split('-').map(Number);
  return `${String(d)} ${MONTHS_ES[m - 1] ?? ''} ${iso.slice(0, 4)}`;
}

/** Stub mínimo de `Center` usado cuando no podemos cargar el endpoint real
 *  (center_admin / staff no tienen acceso a `/api/admin/centers`). Solo se
 *  necesita `id`/`name` para el dropdown y para el body del POST. */
function stubCenter(id: string, name: string): Center {
  return {
    id,
    name,
    slug: '',
    tenantId: '',
    timezone: '',
    createdAt: '',
  };
}

/**
 * Centros que el usuario puede ver / usar como destino al crear reservas.
 *  - superadmin: TODOS los centros (cargados por `useCenters`).
 *  - center_admin: sus `adminCenterIds` (stubs con nombre = "Centro {id corto}"
 *    hasta que el backend exponga un endpoint accesible para center_admin).
 *  - staff: stub con su `centerId` legacy.
 */
function pickAccessibleCenters(
  role: string | undefined,
  userCenterId: string | null | undefined,
  adminCenterIds: string[] | undefined,
  allCenters: readonly Center[] | undefined,
): readonly Center[] {
  if (role === 'superadmin') return allCenters ?? [];
  if (role === 'center_admin') {
    const ids = adminCenterIds && adminCenterIds.length > 0
      ? adminCenterIds
      : (userCenterId ? [userCenterId] : []);
    return ids.map((id) => stubCenter(id, `Centro ${id.slice(0, 6)}`));
  }
  // staff
  if (userCenterId) return [stubCenter(userCenterId, 'Mi centro')];
  return [];
}

// ── BookingsPage ──────────────────────────────────────────────────────────────
export function BookingsPage() {
  const { user, hasRole } = useAuth();
  const isSuper = hasRole('superadmin');
  const isCenterAdmin = hasRole('center_admin');
  const canCreate = isSuper || isCenterAdmin;
  // `GET /api/admin/centers` es superadmin-only (403 para el resto). Para
  // center_admin con N centros no tenemos el array de nombres todavía — el
  // contrato lo resolverá vía session.adminCenterIds + un fetch por id, o
  // exponiendo un endpoint nuevo. Mientras tanto:
  //   - superadmin: usa la lista entera del endpoint.
  //   - center_admin: usa el grant del JWT; los IDs sin nombre se muestran
  //     como "Centro {id corto}" para no bloquear el flujo de creación.
  const { data: allCenters } = useCenters(isSuper);
  const accessibleCenters = useMemo(
    () => pickAccessibleCenters(user?.role, user?.centerId, user?.adminCenterIds, allCenters),
    [user, allCenters],
  );

  // ── Filtros ─────────────────────────────────────────────────────────────────
  // Filtro de centro compartido con el resto de páginas (super/center_admin
  // → `'all'` por defecto; staff → su centro). Hook hace persistencia en
  // sessionStorage para que cambiar de pestaña conserve la elección.
  const { activeCenterId, setActiveCenterId, supportsAll } = useActiveCenter();
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<BookingStatus | 'all'>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [page, setPage] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  // Toggle "Solo mis reservas" — visible si el user es staff (tiene linkedStaffId).
  const [onlyMine, setOnlyMine] = useState<boolean>(false);
  // Drawer de creación
  const [createOpen, setCreateOpen] = useState<boolean>(false);

  // Debounce de búsqueda — server query, no per-keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQ(q.trim()); }, 300);
    return () => { clearTimeout(t); };
  }, [q]);

  // M-9 closeout: antes `new Date().toISOString().slice(0,10)` defaulteaba
  // a UTC del navegador y desalineaba el rango export cuando el admin
  // estaba en una TZ distinta a la del centro. Hoy admin es ES-only así
  // que el bug es teórico, pero el helper hace que sea correcto siempre.
  // Si hay centro activo concreto usamos su TZ; si es "todos" fallback al
  // default del producto (Europe/Madrid).
  const activeCenterTz = useMemo(() => {
    if (activeCenterId === ALL_CENTERS) return 'Europe/Madrid';
    return allCenters?.find((c) => c.id === activeCenterId)?.timezone ?? 'Europe/Madrid';
  }, [activeCenterId, allCenters]);
  const today = todayInTimezone(activeCenterTz);
  const firstOfMonth = today.slice(0, 8) + '01';
  const [exportFrom, setExportFrom] = useState(firstOfMonth);
  const [exportTo, setExportTo] = useState(today);
  const [exporting, setExporting] = useState(false);

  async function handleExport() {
    setExporting(true);
    try {
      await adminReportsApi.exportBookingsCsv(exportFrom, exportTo);
    } finally {
      setExporting(false);
    }
  }

  const effectiveCenterId = activeCenterId === ALL_CENTERS ? undefined : activeCenterId;
  const effectiveStaffId = onlyMine && user?.linkedStaffId ? user.linkedStaffId : undefined;

  const { data, isLoading } = useBookings({
    centerId: effectiveCenterId,
    staffId: effectiveStaffId,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    q: debouncedQ || undefined,
    page: page + 1,
    perPage: 20,
  });

  const bookings = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const openBooking = bookings.find((b) => b.id === openId) ?? null;

  const stats = useMemo(() => ({
    total,
    confirmed: bookings.filter((b) => b.status === 'confirmed').length,
    pending: bookings.filter((b) => b.status === 'pending' || b.status === 'pending_clinic_approval').length,
  }), [bookings, total]);

  // Si solo hay UN centro accesible y el caller es center_admin, el dropdown
  // se reduce a una etiqueta no-interactiva — no ofrece "Todos".
  const showCenterDropdown = accessibleCenters.length >= 1 && (isSuper || accessibleCenters.length > 1);
  const showOnlyMineToggle = !!user?.linkedStaffId;

  // Filtro CENTRO — para la creación necesitamos un centerId concreto.
  const createInitialCenterId: string | null =
    effectiveCenterId ?? (accessibleCenters.length > 0 ? accessibleCenters[0].id : null);
  const centerLockedForCreate = accessibleCenters.length === 1;

  return (
    <div>
      <PageHead
        title="Reservas"
        sub="Listado de reservas a través del widget y del panel."
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <input
              type="date"
              className="input"
              value={exportFrom}
              max={exportTo}
              onChange={(e) => { setExportFrom(e.target.value); }}
              aria-label="Fecha inicio exportación"
            />
            <input
              type="date"
              className="input"
              value={exportTo}
              min={exportFrom}
              onChange={(e) => { setExportTo(e.target.value); }}
              aria-label="Fecha fin exportación"
            />
            <button
              type="button"
              className="btn"
              data-variant="secondary"
              onClick={() => { void handleExport(); }}
              disabled={exporting || !exportFrom || !exportTo}
            >
              <Icons.Download size={14} /> {exporting ? 'Exportando…' : 'Exportar CSV'}
            </button>
            {canCreate && accessibleCenters.length > 0 && (
              <button
                type="button"
                className="btn"
                data-variant="primary"
                onClick={() => { setCreateOpen(true); }}
                title="Crear reserva desde el admin"
              >
                <Icons.Plus size={14} /> Nueva reserva
              </button>
            )}
          </div>
        }
      />

      <div className="stat-row">
        <div className="stat">
          <div className="stat-label">Total</div>
          <div className="stat-value">{total}</div>
          <div className="stat-delta" data-tone="neutral">Reservas totales</div>
        </div>
        <div className="stat">
          <div className="stat-label">Confirmadas</div>
          <div className="stat-value">{stats.confirmed}</div>
          <div className="stat-delta" data-tone="neutral">Esta página</div>
        </div>
        <div className="stat">
          <div className="stat-label">Pendientes</div>
          <div className="stat-value">{stats.pending}</div>
          <div className="stat-delta" data-tone={stats.pending > 0 ? 'warn' : 'neutral'}>
            {stats.pending > 0 ? 'Requieren atención' : 'Ninguna pendiente'}
          </div>
        </div>
        <div className="stat">
          <div className="stat-label">Página</div>
          <div className="stat-value">
            {page + 1}
            <span className="soft mono" style={{ fontSize: 14, marginLeft: 6 }}>/ {totalPages}</span>
          </div>
          <div className="stat-delta" data-tone="neutral">de {totalPages}</div>
        </div>
      </div>

      <div className="toolbar">
        <div className="search">
          <Icons.Search size={14} className="ico" />
          <input
            className="input"
            placeholder="Buscar cliente, servicio o email…"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0); }}
          />
        </div>

        {showCenterDropdown && (
          <select
            className="select"
            style={{ width: 'auto' }}
            value={activeCenterId}
            onChange={(e) => { setActiveCenterId(e.target.value); setPage(0); }}
            aria-label="Filtrar por centro"
            title="Centro"
          >
            {supportsAll && (
              <option value={ALL_CENTERS}>
                {isSuper ? 'Todos los centros' : 'Todos mis centros'}
              </option>
            )}
            {accessibleCenters.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}

        <select
          className="select"
          style={{ width: 'auto' }}
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as BookingStatus | 'all'); setPage(0); }}
        >
          <option value="all">Cualquier estado</option>
          {(Object.entries(STATUS_MAP) as [BookingStatus, { label: string }][]).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <input
          type="date"
          className="input"
          value={dateFrom}
          max={dateTo || undefined}
          onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
          aria-label="Desde"
          title="Desde"
          style={{ width: 'auto' }}
        />
        <input
          type="date"
          className="input"
          value={dateTo}
          min={dateFrom || undefined}
          onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
          aria-label="Hasta"
          title="Hasta"
          style={{ width: 'auto' }}
        />

        {showOnlyMineToggle && (
          <label
            className="btn"
            data-size="sm"
            data-variant={onlyMine ? 'primary' : undefined}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            title="Mostrar solo las reservas asignadas a mí"
          >
            <input
              type="checkbox"
              checked={onlyMine}
              onChange={(e) => { setOnlyMine(e.target.checked); setPage(0); }}
              style={{ marginRight: 4 }}
            />
            Solo mis reservas
          </label>
        )}

        {(dateFrom || dateTo || statusFilter !== 'all' || q || onlyMine) && (
          <button
            type="button"
            className="btn"
            data-size="sm"
            onClick={() => {
              setStatusFilter('all'); setDateFrom(''); setDateTo(''); setQ('');
              setOnlyMine(false); setPage(0);
            }}
            title="Limpiar filtros"
          >
            <Icons.X size={12} /> Limpiar
          </button>
        )}
        <div className="toolbar-spacer" />
        <span className="mono" style={{ fontSize: 11.5, color: 'var(--fg-soft)' }}>
          {total} resultados
        </span>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Servicio</th>
              {activeCenterId === ALL_CENTERS && <th>Centro</th>}
              <th>Profesional</th>
              <th>Cuándo</th>
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={activeCenterId === ALL_CENTERS ? 7 : 6} style={{ textAlign: 'center', padding: 32, color: 'var(--fg-muted)' }}>
                  Cargando…
                </td>
              </tr>
            )}
            {!isLoading && bookings.length === 0 && (
              <tr>
                <td colSpan={activeCenterId === ALL_CENTERS ? 7 : 6} style={{ textAlign: 'center', padding: 32, color: 'var(--fg-muted)' }}>
                  Sin reservas
                </td>
              </tr>
            )}
            {bookings.map((b) => (
              <tr key={b.id} data-clickable="true" onClick={() => { setOpenId(b.id); }}>
                <td>
                  <div className="user-cell">
                    <div className="avatar" data-tone={tone(b.customerName)}>
                      {initials(b.customerName)}
                    </div>
                    <div className="user-cell-info">
                      <span className="user-cell-name">{b.customerName}</span>
                      <span className="user-cell-meta">{b.customerEmail}</span>
                    </div>
                  </div>
                </td>
                <td>
                  <div className="cell-bold">{b.serviceName}</div>
                </td>
                {activeCenterId === ALL_CENTERS && (
                  <td className="cell-muted">{b.centerName}</td>
                )}
                <td className="cell-muted">{b.staffName ?? '—'}</td>
                <td>
                  <div className="cell-bold mono">{fmtDate(b.startTime.slice(0, 10))}</div>
                  <div className="cell-muted mono">{fmtTime(b.startTime)}</div>
                </td>
                <td><StatusPill status={b.status} /></td>
                <td className="cell-narrow">
                  <RowMenuWithActions booking={b} onOpen={() => { setOpenId(b.id); }} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="card-foot">
          <span>
            Página {page + 1} de {totalPages} · mostrando {bookings.length} de {total}
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              type="button"
              className="icon-btn"
              disabled={page === 0}
              onClick={() => { setPage((p) => Math.max(0, p - 1)); }}
            >
              <Icons.ChevL size={14} />
            </button>
            <button
              type="button"
              className="icon-btn"
              disabled={page >= totalPages - 1}
              onClick={() => { setPage((p) => Math.min(totalPages - 1, p + 1)); }}
            >
              <Icons.ChevR size={14} />
            </button>
          </div>
        </div>
      </div>

      {openBooking && (
        <BookingDetailDrawer booking={openBooking} onClose={() => { setOpenId(null); }} />
      )}

      {createOpen && (
        <BookingFormDrawer
          initialCenterId={createInitialCenterId}
          availableCenters={accessibleCenters}
          centerLocked={centerLockedForCreate}
          onClose={() => { setCreateOpen(false); }}
          onCreated={() => { setCreateOpen(false); }}
        />
      )}
    </div>
  );
}
